// src/pages/Footer.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import { getSettings } from "../../services/settingsServices";

// (optionnel) rend /uploads/... absolu vers l’API
const toAbsolute = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s;
  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

function Footer() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    nom_comite: "",
    logo_comite: "",
    adresse_comite: "",
    tel_comite: "",
    mail_comite: "",
    facebook_comite: "",
    instagram_comite: "",
  });
  const [mapUrl, setMapUrl] = useState("");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        if (data.length > 0) {
          setSettings(data[0]);
        }
      } catch (err) {
        console.error("Erreur lors de la récupération des paramètres", err);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (!settings.adresse_comite) {
      setMapUrl("");
      return;
    }
    const encodedAddress = encodeURIComponent(settings.adresse_comite);
    const newMapUrl = `https://www.google.com/maps?q=${encodedAddress}&output=embed`;
    setMapUrl(newMapUrl);
  }, [settings.adresse_comite]);

  // Scroll vers ancre (home) ou navigation si besoin
  const handleNavigate = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-10 bg-slate-950 text-slate-100 border-t border-slate-800/80">
      {/* Contenu principal */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-10">
          {/* Colonne 1 : Map + coordonnées */}
          <div className="md:col-span-2 space-y-3">
            <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-primary mb-1">
              Localisation
            </h3>
            <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/80 shadow-lg">
              {mapUrl ? (
                <iframe
                  title="Localisation du comité"
                  src={mapUrl}
                  width="100%"
                  height="220"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full"
                />
              ) : (
                <div className="h-40 flex items-center justify-center text-xs text-slate-400">
                  Adresse du comité non définie
                </div>
              )}
            </div>

            <div className="mt-2 text-xs sm:text-sm space-y-1.5">
              {settings.adresse_comite && (
                <p className="text-slate-300">
                  <span className="font-semibold">Adresse :</span>{" "}
                  {settings.adresse_comite}
                </p>
              )}
              {settings.mail_comite && (
                <p>
                  <span className="font-semibold">Mail :</span>{" "}
                  <a
                    href={`mailto:${settings.mail_comite}`}
                    className="underline decoration-primary/60 underline-offset-2 hover:text-primary transition-colors"
                  >
                    {settings.mail_comite}
                  </a>
                </p>
              )}
              {settings.tel_comite && (
                <p className="text-slate-300">
                  <span className="font-semibold">Téléphone :</span>{" "}
                  {settings.tel_comite}
                </p>
              )}
            </div>
          </div>

          {/* Colonne 2 : Logo + nom comité */}
          <div className="flex flex-col items-center text-center space-y-3">
            {settings.logo_comite && (
              <div className="rounded-2xl bg-gray-300/80 border border-slate-800 shadow-lg px-4 py-4 flex flex-col items-center justify-center w-full max-w-xs">
                <img
                  src={toAbsolute(settings.logo_comite)}
                  alt={`Logo ${settings.nom_comite}`}
                  className="h-30 w-auto object-contain mb-3"
                />
                <h2 className="text-sm md:text-base font-semibold leading-snug">
                  {settings.nom_comite || "Comité Départemental de Judo"}
                </h2>
              </div>
            )}
            {!settings.logo_comite && (
              <h2 className="text-sm md:text-base font-semibold leading-snug">
                {settings.nom_comite || "Comité Départemental de Judo"}
              </h2>
            )}
            <p className="text-[11px] text-slate-400 max-w-xs">
              Au service des clubs, enseignants, arbitres et judokas du
              département.
            </p>
          </div>

          {/* Colonne 3 : navigation site + réseaux */}
          <div className="flex flex-col justify-between space-y-6 md:space-y-0">
            <div>
              <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-primary mb-3">
                Navigation
              </h3>
              <nav className="flex flex-col gap-1.5 text-sm">
                <button
                  type="button"
                  onClick={() => handleNavigate("about")}
                  className="text-slate-300 hover:text-primary text-left transition-colors"
                >
                  A propos
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigate("news")}
                  className="text-slate-300 hover:text-primary text-left transition-colors"
                >
                  Actualités
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigate("events")}
                  className="text-slate-300 hover:text-primary text-left transition-colors"
                >
                  Événements
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigate("galerie")}
                  className="text-slate-300 hover:text-primary text-left transition-colors"
                >
                  Galerie
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigate("contact")}
                  className="text-slate-300 hover:text-primary text-left transition-colors"
                >
                  Contact
                </button>
              </nav>
            </div>

            <div>
              <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-primary mb-3">
                Réseaux sociaux
              </h3>
              <div className="flex items-center gap-3">
                {settings.facebook_comite && (
                  <a
                    href={settings.facebook_comite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-700 hover:border-primary/80 hover:bg-primary/10 transition-colors"
                  >
                    <FaFacebookF className="text-lg" />
                  </a>
                )}
                {settings.instagram_comite && (
                  <a
                    href={settings.instagram_comite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-700 hover:border-primary/80 hover:bg-primary/10 transition-colors"
                  >
                    <FaInstagram className="text-lg" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Colonne 4 : liens légaux */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-primary mb-1">
              Informations
            </h3>
            <nav className="flex flex-col gap-1.5 text-sm">
              <a
                href="/reglement-interieur"
                className="text-slate-300 hover:text-primary transition-colors"
              >
                Règlement intérieur
              </a>
              <a
                href="/mentions-legales"
                className="text-slate-300 hover:text-primary transition-colors"
              >
                Mentions légales
              </a>
              <a
                href="/politique-confidentialite"
                className="text-slate-300 hover:text-primary transition-colors"
              >
                Politique de confidentialité
              </a>
              <a
                href="/conditions-utilisation"
                className="text-slate-300 hover:text-primary transition-colors"
              >
                Conditions d’utilisation
              </a>
              <a
                href="/faq"
                className="text-slate-300 hover:text-primary transition-colors"
              >
                FAQ
              </a>
            </nav>
          </div>
        </div>

        {/* Barre du bas */}
        <div className="mt-8 pt-4 border-t border-slate-800/80 flex flex-col md:flex-row items-center justify-between gap-2 text-[11px] md:text-xs text-slate-500">
          <p className="text-center md:text-left">
            Copyright © {currentYear} - Tous droits réservés -{" "}
            <a
              href="https://www.monappliclub.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-primary/60 underline-offset-2 hover:text-primary transition-colors"
            >
              MonAppliClub
            </a>{" "}
            -{" "}
            <a
              href="https://profile.vkard.io/8f8e4d95-cf28-40b0-afc6-2ec677889d49/ab099714aa"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-primary/60 underline-offset-2 hover:text-primary transition-colors"
            >
              Laurent VICTORIN
            </a>
          </p>
          <p className="text-center md:text-right">
            Application MonAppliComité – Gestion des comités départementaux de Judo.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
